import {
  Box,
  Button,
  HStack,
  Flex,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Text,
  IconButton,
  VStack,
  Badge,
  Switch,
  Tooltip,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { AiOutlinePlus } from 'react-icons/ai';
import { BsFillTrashFill } from 'react-icons/bs';
import { NumericFormat } from 'react-number-format';
import { useParams, useHistory } from 'react-router-dom';
import { z } from 'zod';
import Form from 'components/form';
import FormInput from 'components/form/Input';
import useApi from 'hooks/useApi';
import { Api } from 'lib/api';
import formatRupiah from 'lib/formatRupiah';
import useStore from 'store/useStore';

// Validation schema (reuse from create page)
const productDigitalSchema = z.object({
  title: z.string().min(0, 'Title is required'),
  description: z.string().optional(),
  price: z
    .string()
    .regex(/^\d+$/)
    .refine((value) => parseFloat(value) >= 0, {
      message: 'Price must number',
    }),
  category: z.string().min(0, 'Category is required'),
  thumbnail: z.string().min(0, 'Thumbnail URL is required'),
  isActive: z.boolean().default(true),
  tags: z.array(z.string()).optional(),
  content: z
    .array(
      z.object({
        title: z.string().nonempty('Content title is required'),
        description: z.string().optional(),
        youtubeUrl: z
          .string()
          .url('Invalid URL')
          .refine((url) => /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.*/.test(url), {
            message: 'Invalid YouTube URL format',
          }),
        isPrivate: z.boolean().default(false),
        duration: z.number().optional(),
        order: z.number().optional(),
      }),
    )
    .min(1, 'At least one content item is required'),
});

const ProductDigitalEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const [tagInput, setTagInput] = useState('');
  const { getDigitalProductsDetail, digitalProductsDetail } = useStore();
  const { loading, error } = useApi({
    isImmediate: true,
    fetcher: getDigitalProductsDetail,
    params: { productId: id },
  });

  const form = useForm({
    resolver: zodResolver(productDigitalSchema),
    defaultValues: {
      ...digitalProductsDetail,
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = form;

  const { fields, append, remove } = useFieldArray({ control, name: 'content' });

  // Load data into form when digitalProductsDetail is available
  useEffect(() => {
    if (digitalProductsDetail?.data) {
      const product = digitalProductsDetail.data;

      reset({
        title: product.title,
        description: product.description,
        price: String(product.price),
        category: product.category,
        thumbnail: product.thumbnail,
        isActive: product.isActive,
        tags: product.tags || [],
        content: product.content.map((item) => ({
          title: item.title,
          description: item.description || '',
          youtubeUrl: item.youtubeUrl,
          isPrivate: item.isPrivate,
          duration: item.duration || 0,
          order: item.order || 0,
        })),
      });
    }
  }, [digitalProductsDetail, reset]);

  const onSubmit = async (values) => {
    try {
      await Api.put({ url: `/admin/digital-products/${id}`, data: values });
      toast.success('Digital product created successfully!');
      history.push('/admin/digital-products');
    } catch (error) {
      toast.error('Failed to update product: ' + error.message);
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = watch('tags') || [];
    setValue(
      'tags',
      currentTags.filter((tag) => tag !== tagToRemove),
    );
  };

  const [categoryList, getCategoryHandler] = useStore((s) => [s.categoryList, s.getCategory]);

  // Filter only digital categories
  const mappingCategoryList = categoryList?.data?.map((result) => ({
    label: result.name,
    ...result,
  }));

  // Fetch categories when component mounts
  useEffect(() => {
    getCategoryHandler({
      page: 1,
      limit: 50,
      type: 'digital',
    });
  }, []);

  const addTag = () => {
    if (tagInput.trim()) {
      const currentTags = watch('tags') || [];
      if (!currentTags.includes(tagInput.trim())) {
        setValue('tags', [...currentTags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  if (loading)
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }} textAlign="center">
        <Flex justify="center" align="center" direction="column" minH="200px">
          <Text fontSize="xl" fontWeight="bold" mb={6}>
            Loading Digital Product...
          </Text>
        </Flex>
      </Box>
    );

  if (error)
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Flex justify="center" align="center" direction="column" minH="200px">
          <Text color="red.500" fontSize="xl">
            Error loading product: {error.message}
          </Text>
          <Button mt={4} onClick={() => history.goBack()}>
            Go Back
          </Button>
        </Flex>
      </Box>
    );

  const product = digitalProductsDetail?.data;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} backgroundColor={'white'} px={4}>
      <Flex justify="space-between" mb={6} align="center">
        <Text fontSize="2xl" fontWeight="bold">
          Edit Digital Product
        </Text>

        {product && (
          <Flex gap={2} align="center">
            <Box px={3} py={1} bg="gray.100" borderRadius="md">
              <Text fontSize="sm" fontWeight="medium">
                {product.purchaseCount || 0} Purchases
              </Text>
            </Box>
            <Box px={3} py={1} bg="yellow.100" borderRadius="md" display="flex" alignItems="center">
              <Text fontSize="sm" fontWeight="medium">
                Rating: {product.ratings?.average || 0} ({product.ratings?.count || 0} reviews)
              </Text>
            </Box>
          </Flex>
        )}
      </Flex>

      <Form methods={form} onSubmit={handleSubmit(onSubmit)}>
        <Flex
          borderRadius={'2xl'}
          px={{ base: 4, md: 10 }}
          py={10}
          flexDirection={'column'}
          backgroundColor={'white'}
          boxShadow="md"
        >
          <Text mb={4} fontSize="xl" fontWeight="bold">
            Basic Information
          </Text>

          <FormInput
            propsFormControl={{ mb: 4 }}
            label="Product Title"
            inputProps={{
              ...form.register('title'),
              name: 'title',
              placeholder: 'Enter product title',
            }}
            errors={errors}
            type="TEXT"
          />

          <FormInput
            propsFormControl={{ mb: 4 }}
            label="Description"
            inputProps={{
              ...form.register('description'),
              name: 'description',
              placeholder: 'Enter product description',
            }}
            errors={errors}
            type="TEXTAREA"
          />

          <HStack
            mb={4}
            justifyContent={'space-between'}
            alignItems={'flex-start'}
            flexDirection={{ base: 'column', md: 'row' }}
            spacing={{ base: 4, md: 8 }}
          >
            <FormControl flex="1" isInvalid={errors?.price}>
              <FormLabel htmlFor="price">Price</FormLabel>
              <NumericFormat
                {...form.register('price')}
                placeholder={formatRupiah('50000')}
                style={{ marginTop: 3 }}
                className="input-custom"
                valueIsNumericString={true}
                prefix="Rp. "
                value={form.watch('price') ?? 0}
                onValueChange={({ floatValue }) => {
                  setValue('price', String(floatValue || 0));
                }}
                thousandsGroupStyle="thousand"
                thousandSeparator=","
              />
              <FormErrorMessage>{errors?.price && errors?.price?.message}</FormErrorMessage>
            </FormControl>

            <FormInput
              propsFormControl={{
                mb: 4,
                flex: '1',
              }}
              label="Category"
              inputProps={{
                ...form.register('category'),
                name: 'category',
                value: mappingCategoryList?.find((item) => item._id === form.watch('category')),
                onChange: (value) => setValue('category', value?._id),
                placeholder: 'Select Category',
                optionsData: mappingCategoryList,
              }}
              type="SELECT"
              errors={errors}
            />
          </HStack>

          <FormInput
            propsFormControl={{ mb: 4 }}
            label="Thumbnail URL"
            inputProps={{
              ...form.register('thumbnail'),
              name: 'thumbnail',
              placeholder: 'Enter thumbnail image URL',
            }}
            errors={errors}
            type="TEXT"
          />

          <FormControl mb={4} display="flex" alignItems="center">
            <FormLabel htmlFor="isActive" mb="0">
              Product Active
            </FormLabel>
            <Switch
              id="isActive"
              {...form.register('isActive')}
              isChecked={watch('isActive')}
              onChange={(e) => setValue('isActive', e.target.checked)}
              colorScheme="blue"
            />
          </FormControl>

          <FormControl mb={4} isInvalid={errors?.tags}>
            <FormLabel htmlFor="tags">Tags (Press Enter to Add)</FormLabel>
            <HStack>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder="Add tags..."
                id="tags"
              />
              <Button onClick={addTag} colorScheme="blue">
                Add
              </Button>
            </HStack>
            <Flex mt={2} flexWrap="wrap" gap={2}>
              {(watch('tags') || []).map((tag, index) => (
                <Badge
                  key={index}
                  colorScheme="blue"
                  borderRadius="full"
                  px={2}
                  py={1}
                  display="flex"
                  alignItems="center"
                >
                  {tag}
                  <Box as="span" ml={1} cursor="pointer" onClick={() => removeTag(tag)}>
                    ×
                  </Box>
                </Badge>
              ))}
            </Flex>
          </FormControl>
        </Flex>

        <Flex
          borderRadius={'2xl'}
          px={{ base: 4, md: 10 }}
          py={10}
          mt={5}
          flexDirection={'column'}
          backgroundColor={'white'}
          boxShadow="md"
        >
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Text fontSize="xl" fontWeight="bold">
              Content Items
            </Text>
            <Button
              leftIcon={<AiOutlinePlus />}
              colorScheme="blue"
              size="sm"
              onClick={() =>
                append({
                  title: '',
                  description: '',
                  youtubeUrl: '',
                  isPrivate: false,
                  duration: 0,
                  order: fields.length,
                })
              }
            >
              Add Content
            </Button>
          </Flex>

          {fields.map((field, index) => (
            <Box
              key={field.id}
              p={4}
              mb={4}
              borderWidth="1px"
              borderRadius="md"
              position="relative"
              boxShadow="sm"
              _hover={{ boxShadow: 'md' }}
              transition="all 0.2s"
            >
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontWeight="medium">Content #{index + 1}</Text>
                <IconButton
                  icon={<BsFillTrashFill />}
                  colorScheme="red"
                  size="sm"
                  isDisabled={fields.length === 1}
                  onClick={() => remove(index)}
                  aria-label="Remove content item"
                />
              </Flex>

              <VStack spacing={4} align="stretch">
                <FormInput
                  propsFormControl={{ mb: 2 }}
                  label="Title"
                  inputProps={{
                    ...form.register(`content.${index}.title`),
                    name: `content.${index}.title`,
                    placeholder: 'Content title',
                  }}
                  errors={errors?.content?.[index]}
                  type="TEXT"
                />

                <FormInput
                  propsFormControl={{ mb: 2 }}
                  label="Description"
                  inputProps={{
                    ...form.register(`content.${index}.description`),
                    name: `content.${index}.description`,
                    placeholder: 'Content description',
                  }}
                  errors={errors?.content?.[index]}
                  type="TEXTAREA"
                />

                <FormInput
                  propsFormControl={{ mb: 2 }}
                  label="YouTube URL"
                  inputProps={{
                    ...form.register(`content.${index}.youtubeUrl`),
                    name: `content.${index}.youtubeUrl`,
                    placeholder: 'https://youtube.com/...',
                  }}
                  errors={errors?.content?.[index]}
                  type="TEXT"
                />

                <HStack spacing={8}>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor={`content.${index}.isPrivate`} mb="0">
                      Private Content
                    </FormLabel>
                    <Tooltip label="Mark as private if this content is premium">
                      <Switch
                        id={`content.${index}.isPrivate`}
                        {...form.register(`content.${index}.isPrivate`)}
                        isChecked={watch(`content.${index}.isPrivate`)}
                        onChange={(e) => setValue(`content.${index}.isPrivate`, e.target.checked)}
                        colorScheme="blue"
                      />
                    </Tooltip>
                  </FormControl>

                  <FormControl>
                    <FormLabel htmlFor={`content.${index}.duration`}>Duration (minutes)</FormLabel>
                    <NumberInput min={0}>
                      <NumberInputField
                        id={`content.${index}.duration`}
                        {...form.register(`content.${index}.duration`, {
                          valueAsNumber: true,
                        })}
                        placeholder="Duration in minutes"
                      />
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel htmlFor={`content.${index}.order`}>Display Order</FormLabel>
                    <NumberInput min={0}>
                      <NumberInputField
                        id={`content.${index}.order`}
                        {...form.register(`content.${index}.order`, {
                          valueAsNumber: true,
                        })}
                        placeholder="Order number"
                      />
                    </NumberInput>
                  </FormControl>
                </HStack>
              </VStack>
            </Box>
          ))}
          {errors?.content?.message && (
            <Text color="red.500" mb={4}>
              {errors.content.message}
            </Text>
          )}
        </Flex>

        <Button
          mt={6}
          mb={6}
          colorScheme="blue"
          type="submit"
          size="lg"
          width={{ base: '100%', md: 'auto' }}
          isLoading={isSubmitting}
        >
          Update Digital Product
        </Button>
      </Form>
    </Box>
  );
};

export default ProductDigitalEdit;
